import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarHeart, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";
import { ideasStore } from "@/lib/storage";
import { OverallScore, ScoreBars } from "@/components/Scores";
import { DailyPushToggle } from "@/components/DailyPushToggle";

export const IdeaOfTheDay = () => {
  const [idea, setIdea] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/idea-of-the-day`).then((r) => setIdea(r.data.idea)).catch(() => setError(true));
  }, []);

  const open = () => {
    ideasStore.upsert([idea]);
    navigate(`/idea/${idea.id}`);
  };

  if (error) return null;
  const date = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="idea-of-the-day">
      <div className="rounded-3xl border border-white/[0.07] bg-[#12141F] p-8 sm:p-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 overline-label"><CalendarHeart className="w-4 h-4" /> Idea del giorno · {date}</div>
          {idea ? (
            <>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-4" data-testid="daily-idea-name">{idea.nome}</h2>
              <p className="text-slate-300 mt-4 leading-relaxed">{idea.pitch}</p>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{idea.descrizione}</p>
              <div className="flex flex-wrap gap-3 mt-7">
                <button onClick={open} data-testid="daily-idea-open-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400">
                  Apri l'idea completa <ArrowUpRight className="w-4 h-4" />
                </button>
                <Link to="/genera" className="inline-flex items-center rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-300" data-testid="daily-idea-more-btn">
                  Generane di tue
                </Link>
                <Link to="/idee-del-giorno" className="inline-flex items-center px-2 py-3 text-sm font-semibold text-slate-400 transition-colors hover:text-amber-300" data-testid="daily-idea-archive-link">
                  Archivio →
                </Link>
                <DailyPushToggle />
              </div>
            </>
          ) : (
            <div className="mt-5 space-y-3 animate-pulse" data-testid="daily-idea-loading">
              <div className="h-8 w-2/3 rounded-lg bg-white/5" />
              <div className="h-4 w-full rounded bg-white/5" />
              <div className="h-4 w-5/6 rounded bg-white/5" />
              <p className="text-xs text-slate-500 pt-2">L'AI sta forgiando l'idea di oggi…</p>
            </div>
          )}
        </div>
        {idea && (
          <div className="lg:col-span-5 rounded-2xl bg-[#0d0f17] border border-white/5 p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-amber-300">{idea.categoria}</span>
              <OverallScore value={idea.punteggio_complessivo} testId="daily-idea-score" />
            </div>
            <div className="mt-5"><ScoreBars scores={idea.punteggi} testPrefix="daily-idea" /></div>
          </div>
        )}
      </div>
    </section>
  );
};
